/**
 * FlashBook — frames from a film.
 *
 * The cover trimmer shows the film it is trimming as a **strip of its own
 * frames**, because a bar with a thumb on it says *where in the timeline you are*
 * and a strip says *which part of the film you are keeping*. That is the whole
 * difference between a slider and an editor, and it is the one thing a creator
 * needs in order to choose sixty seconds out of two minutes: they have to be able
 * to see the moment they are cutting to.
 *
 * Two implementations, one shape:
 *
 *  - **web** (`flashbookFilmFramesImpl.ts`) — seek a detached `<video>` and draw
 *    the frame to a canvas. The browser has had exactly this capability forever;
 *    it is only ever reached through an element, which is why the frames arrive as
 *    data URLs rather than as files.
 *  - **native** (`flashbookFilmFramesImpl.native.ts`) — `expo-video-thumbnails`,
 *    which is the platform's own decoder and the only way to ask a device for one
 *    frame of a film.
 *
 * Reading frames is *best effort* and always has been: a film can be
 * cross-origin, half-downloaded, or encoded in something the platform will not
 * open. Every failure below ends as \"no frame here\" and the strip draws its own
 * placeholder, because a trimmer that refuses to open because it could not
 * decorate itself would be a far worse trade than a strip with gaps in it.
 */
import {createPlatformFilmFrameReader} from '#/lib/flashbookFilmFramesImpl'

/**
 * How many frames a strip shows.
 *
 * Eight, because the strip has to hold a whole film inside a phone's width and a
 * frame much narrower than ~40px stops reading as a picture of anything — the
 * strip becomes a smear, which is what it was before. Eight cells also survive
 * the sliding window: the 60-second window covers at least two thirds of any
 * film that needs trimming, so there is always a frame at each end of the
 * selection to judge it by.
 */
export const FILM_STRIP_FRAMES = 8

/**
 * How wide a film's *still* is decoded.
 *
 * A strip cell is forty pixels, so the strip reads frames at 160 and no bigger.
 * A still is a different job — it is the picture a book is recognised by in a
 * feed, painted across a card — so it is read at the size it is shown at rather
 * than at the size a thumbnail happens to be.
 */
export const FILM_POSTER_WIDTH = 720

/**
 * How far past the asked-for instant a still is looked for again.
 *
 * A decoder often holds no picture at an exact seek — the first and last frames
 * of a file are the ones this module already avoids sampling — so a read that
 * comes back empty is retried a second further in rather than treated as
 * "this film has no picture".
 */
export const FILM_STILL_RETRY_MS = 1000

/**
 * Where in a film each frame is taken from, as fractions of its whole length.
 *
 * The **centre** of each cell rather than its edge, for two reasons: the first and
 * last frames of a file are the ones a decoder is most likely to fail on, and the
 * middle of a cell is what a viewer reads that cell as standing for. Sampling at
 * `i / (n - 1)` would put a frame exactly on the film's final instant, which many
 * encoders hold no picture for at all.
 */
export function filmFrameFractions(
  count: number = FILM_STRIP_FRAMES,
): number[] {
  if (count < 1) return []
  return Array.from({length: count}, (_, i) => (i + 0.5) / count)
}

/** The same fractions, in milliseconds of a film of this length. */
export function filmFrameTimesMs(
  durationMs: number,
  count: number = FILM_STRIP_FRAMES,
): number[] {
  if (!Number.isFinite(durationMs) || durationMs <= 0) return []
  return filmFrameFractions(count).map(fraction =>
    Math.min(durationMs, Math.round(durationMs * fraction)),
  )
}

/** What a platform has to be able to do to draw a filmstrip. */
export type FilmFrameReader = {
  /**
   * One entry per requested time, in order, each a URL an `<Image>` can show.
   *
   * `undefined` where that frame could not be read. Never throws and never
   * rejects — a partial strip is the expected outcome, not an error.
   */
  read: (uri: string, timesMs: number[]) => Promise<(string | undefined)[]>
  /**
   * One frame, at the size a still is shown at — the picture on a cover card.
   *
   * A separate read from `read` rather than "the first of the strip" because the
   * two are asked for differently: a strip wants eight small frames and will wait
   * for the film to be pulled down for them, while a card wants one large frame and
   * must not drag a whole minute of video into a scrolling feed to get it.
   */
  readPoster: (
    uri: string,
    atMs: number,
    widthPx: number,
  ) => Promise<string | undefined>
  /** Drops whatever the reader is holding for this film. */
  release: (uri: string) => void
}

/**
 * The still of a film, as a URL an `<Image>` can paint.
 *
 * The one place that decides what a film's still *is*, so the studio writing one
 * into a cover record and a card drawing one for a record that has none cannot
 * disagree about which moment the film opens on.
 *
 * Best effort like every other read here: a film that will not decode, or is
 * served without the headers a canvas needs, yields `undefined` and the caller
 * keeps whatever it had — a book with no still is a plain card, and that is a far
 * better outcome than a publish that fails on a decoration.
 */
export async function readFilmStill(
  uri: string,
  atMs: number = 0,
  widthPx: number = FILM_POSTER_WIDTH,
): Promise<string | undefined> {
  const reader = getFilmFrameReader()
  const first = await reader.readPoster(uri, Math.max(0, atMs), widthPx)
  if (first) return first
  return reader.readPoster(
    uri,
    Math.max(0, atMs) + FILM_STILL_RETRY_MS,
    widthPx,
  )
}

/** The platform's reader, built once. */
const platformReader: FilmFrameReader = createPlatformFilmFrameReader()

/** Testing seam — reading frames is a platform fact, so tests replace it. */
let override: FilmFrameReader | null = null

export function setFilmFrameReader(reader: FilmFrameReader | null): void {
  override = reader
}

export function getFilmFrameReader(): FilmFrameReader {
  return override ?? platformReader
}
